import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  FileText,
  Download,
  Filter,
  Calendar,
  Building2,
  Search,
  FileSpreadsheet,
  FileType,
  FileDown,
  RefreshCw,
  TrendingUp,
  CheckCircle,
  XCircle,
  AlertCircle,
  BarChart3,
  Eye,
  CheckSquare,
  Square,
  ExternalLink
} from 'lucide-react';
import axios from 'axios';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { Document, Packer, Paragraph, Table, TableRow, TableCell, TextRun, WidthType, AlignmentType, HeadingLevel, ImageRun } from 'docx';
import RobotoBase64 from '../fonts/Roboto-Regular';
import html2canvas from 'html2canvas';
import ReportCharts from './ReportCharts';
import './Reports.css';

const Reports = () => {
  const { user } = useAuth();
  const [history, setHistory] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  // Фильтры
  const [selectedProject, setSelectedProject] = useState(() => {
    const saved = localStorage.getItem('selectedProject');
    return (saved === 'unassigned') ? 'all' : (saved || 'all');
  });
  const [selectedEngine, setSelectedEngine] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showAllRecords, setShowAllRecords] = useState(false);

  // Режим выбора и выбранные записи для экспорта
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Статистика
  const [stats, setStats] = useState({
    total: 0,
    completed: 0,
    errors: 0,
    brandFoundCount: 0,
    brandCheckedCount: 0,
    siteFoundCount: 0,
    siteCheckedCount: 0
  });

  // Модальное окно предпросмотра
  // Модальное окно предпросмотра и настроек
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [reportConfig, setReportConfig] = useState({
    includeCharts: true,
    includeAIRecommendations: true,
    includeAIComments: false,
    reportType: 'detailed' // 'detailed' | 'summary'
  });

  const [showPreview, setShowPreview] = useState(false);
  const [previewFormat, setPreviewFormat] = useState('pdf');

  // Модальное окно и настройки для Excel
  const [showExcelConfigModal, setShowExcelConfigModal] = useState(false);
  const [excelConfig, setExcelConfig] = useState({
    includeDate: false,
    includeQuery: true,
    includeRegion: false,
    includeEngine: true,
    includeBrandScore: true,
    includeSiteMentioned: true,
    includeProject: false,
    includeAliceResponse: true,
    includeAIComment: true,
    includeAIRecommendations: false
  });


  // Refs для захвата графиков (2 части)
  const chartsRef1 = React.useRef(null);
  const chartsRef2 = React.useRef(null);

  // ... (existing fetchHistory etc)




  // Загрузка истории проверок
  const fetchHistory = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/check-history', {
        params: {
          limit: 500,
          project_id: selectedProject,
          search_engine: selectedEngine,
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
          search: searchQuery || undefined
        }
      });

      const data = response.data.data || response.data;
      if (Array.isArray(data)) {
        setHistory(data);
      } else {
        setHistory([]);
      }
    } catch (error) {
      console.error('Error fetching history:', error);
      setHistory([]);
    } finally {
      setLoading(false);
    }
  }, [selectedProject, selectedEngine, dateFrom, dateTo, searchQuery]);

  // Загрузка проектов
  const fetchProjects = useCallback(async () => {
    try {
      const response = await axios.get('/api/projects');
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  }, []);

  useEffect(() => {
    if (user) {
      fetchHistory();
      fetchProjects();
    }
  }, [user, fetchHistory, fetchProjects]);

  // Фильтрация данных
  const filteredData = useMemo(() => {
    const filtered = history.filter(check => {
      // Фильтр по проекту
      if (selectedProject !== 'all' && check.project_id?.toString() !== selectedProject) {
        return false;
      }

      // Фильтр по поисковой системе
      if (selectedEngine !== 'all' && check.search_engine !== selectedEngine) {
        return false;
      }

      // Фильтр по дате
      if (dateFrom) {
        const checkDate = new Date(check.check_date);
        const fromDate = new Date(dateFrom);
        if (checkDate < fromDate) return false;
      }

      if (dateTo) {
        const checkDate = new Date(check.check_date);
        const toDate = new Date(dateTo);
        toDate.setHours(23, 59, 59, 999);
        if (checkDate > toDate) return false;
      }

      // Поиск по тексту запроса
      if (searchQuery && !check.query?.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false;
      }

      return true;
    });

    // Сортировка по дате (новые сначала). Раньше группировалось по площадке,
    // из-за чего в превью (первые 20 строк) показывался только Google, а Yandex
    // уезжал в конец и был не виден — это создавало иллюзию что фильтр "Все"
    // не работает.
    return filtered.sort((a, b) => {
      const dateDiff = new Date(b.check_date) - new Date(a.check_date);
      if (dateDiff !== 0) return dateDiff;
      if (a.project_id !== b.project_id) {
        return (a.project_id || 0) - (b.project_id || 0);
      }
      if (a.search_engine !== b.search_engine) {
        return a.search_engine === 'google' ? -1 : 1;
      }
      return 0;
    });
  }, [history, selectedProject, selectedEngine, dateFrom, dateTo, searchQuery]);

  // Подсчёт статистики
  useEffect(() => {
    const completed = filteredData.filter(c => c.status === 'completed').length;
    const errors = filteredData.filter(c => c.status === 'error').length;
    const brandChecked = filteredData.filter(c => c.brand_score !== null);
    const brandFound = brandChecked.filter(c => c.brand_score >= 1).length;
    const siteChecked = filteredData.filter(c => c.site_mentioned !== null && c.site_mentioned !== undefined);
    const siteFound = siteChecked.filter(c => c.site_mentioned === 1 || c.site_mentioned === true);

    setStats({
      total: filteredData.length,
      completed,
      errors,
      brandFoundCount: brandFound,
      brandCheckedCount: brandChecked.length,
      siteFoundCount: siteFound.length,
      siteCheckedCount: siteChecked.length
    });
  }, [filteredData]);

  // Функции для работы с выбором записей
  const toggleSelectAll = () => {
    if (selectedIds.size === filteredData.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredData.map(d => d.id)));
    }
  };

  const toggleSelectItem = (id) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const clearSelection = () => {
    setSelectionMode(false);
    setSelectedIds(new Set());
  };

  // Данные для экспорта (выбранные или все)
  const dataForExport = useMemo(() => {
    if (selectedIds.size > 0) {
      return filteredData.filter(item => selectedIds.has(item.id));
    }
    return filteredData;
  }, [filteredData, selectedIds]);

  // Форматирование даты
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('ru-RU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDateShort = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ru-RU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  // Получение названия проекта
  const getProjectName = (projectId) => {
    if (!projectId) return 'Без проекта';
    const project = projects.find(p => p.id === projectId);
    return project?.title || 'Неизвестный проект';
  };

  // Статус на русском
  const getStatusText = (status) => {
    switch (status) {
      case 'completed': return 'Завершено';
      case 'error': return 'Ошибка';
      case 'running': return 'Выполняется';
      case 'pending': return 'В очереди';
      default: return status;
    }
  };

  // Поисковая система на русском
  const getEngineText = (engine) => {
    switch (engine) {
      case 'google': return 'Google';
      case 'yandex': return 'Яндекс';
      default: return engine;
    }
  };

  // Конвертация изображения в base64
  const loadImageAsBase64 = async (url) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error('Error loading image:', error);
      return null;
    }
  };

  // Экспорт в PDF
  const handleExportClick = () => {
    setShowConfigModal(true);
  };

  // Экспорт в PDF
  const generatePDF = async () => {
    setShowConfigModal(false);
    setExporting(true);
    try {
      const doc = new jsPDF('landscape', 'mm', 'a4');
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();

      // Регистрируем шрифт Roboto
      doc.addFileToVFS('Roboto-Regular.ttf', RobotoBase64);
      doc.addFileToVFS('Roboto-Regular.ttf', RobotoBase64);
      doc.addFont('Roboto-Regular.ttf', 'Roboto', 'normal');
      doc.addFont('Roboto-Regular.ttf', 'Roboto', 'bold');
      doc.setFont('Roboto');

      // Загружаем изображения
      const headerImg = await loadImageAsBase64('/avi-header.png');
      const footerImg = await loadImageAsBase64('/avi-footer.png');

      const addHeaderFooter = (pageNum, totalPages) => {
        doc.setFont('Roboto');
        if (headerImg) doc.addImage(headerImg, 'PNG', 10, 5, 180, 15.3);
        doc.setFontSize(10);
        doc.setTextColor(100);
        doc.text(`Дата: ${new Date().toLocaleDateString('ru-RU')}`, pageWidth - 10, 12, { align: 'right' });
        const footerY = pageHeight - 6;
        doc.setFontSize(8);
        doc.text(`Страница ${pageNum} из ${totalPages}`, 10, footerY, { align: 'left' });
        if (footerImg) {
          const footerImgWidth = 160;
          const footerImgHeight = 7;
          doc.addImage(footerImg, 'PNG', (pageWidth - footerImgWidth) / 2, footerY - 5, footerImgWidth, footerImgHeight);
        }
        doc.text('Источник: AVI Analytics', pageWidth - 10, footerY, { align: 'right' });
      };

      let yPos = 35;
      doc.setFontSize(18);
      doc.setTextColor(40);
      doc.text('Отчёт по проверкам поисковых систем', 10, 28, { align: 'left' });

      // -- БЛОК ГРАФИКОВ (Часть 1: Статистика + Тренды) --
      if (reportConfig.includeCharts && chartsRef1.current) {
        try {
          await new Promise(resolve => setTimeout(resolve, 800)); // Ждем рендера
          const canvas1 = await html2canvas(chartsRef1.current, { scale: 2, useCORS: true, logging: false, backgroundColor: '#ffffff' });
          const imgData1 = canvas1.toDataURL('image/png');
          const imgWidth = pageWidth - 20;
          const imgHeight1 = (canvas1.height * imgWidth) / canvas1.width;

          if (yPos + imgHeight1 > pageHeight - 20) {
            if (yPos > 40) { doc.addPage(); yPos = 25; }
          }
          doc.addImage(imgData1, 'PNG', 10, yPos, imgWidth, imgHeight1);
          yPos += imgHeight1 + 10;
        } catch (err) { console.error("Error charts part 1:", err); }
      }

      // -- БЛОК ГРАФИКОВ (Часть 2: Сравнение Google vs Яндекс) --
      if (reportConfig.includeCharts && chartsRef2.current) {
        try {
          const canvas2 = await html2canvas(chartsRef2.current, { scale: 2, useCORS: true, logging: false, backgroundColor: '#ffffff' });
          const imgData2 = canvas2.toDataURL('image/png');
          const imgWidth = pageWidth - 20;
          const imgHeight2 = (canvas2.height * imgWidth) / canvas2.width;

          // Перенос на новую страницу если нужно
          if (yPos + imgHeight2 > pageHeight - 20 || (pageHeight - yPos) < (pageHeight / 3)) {
            doc.addPage();
            yPos = 25;
          }
          doc.addImage(imgData2, 'PNG', 10, yPos, imgWidth, imgHeight2);
          yPos += imgHeight2 + 10;
        } catch (err) { console.error("Error charts part 2:", err); }
      }

      // -- Фильтры и таблица всегда с новой страницы (страница 3+) --
      doc.addPage();
      yPos = 30;

      const filtersText = [
        selectedProject !== 'all' ? `Проект: ${getProjectName(parseInt(selectedProject))}` : null,
        selectedEngine !== 'all' ? `Поиск: ${getEngineText(selectedEngine)}` : null,
        dateFrom ? `С: ${dateFrom}` : null,
        dateTo ? `По: ${dateTo}` : null
      ].filter(Boolean);

      if (filtersText.length > 0) {
        doc.setFontSize(9);
        doc.setTextColor(100);
        doc.text(`Фильтры: ${filtersText.join(', ')}`, 10, yPos);
        yPos += 8;
      }

      // -- ТАБЛИЦА --
      const tableData = dataForExport.map(check => [
        formatDateShort(check.check_date),
        check.query?.substring(0, 50) || '',
        check.region || '',
        getEngineText(check.search_engine),
        check.brand_score !== null ? (check.brand_score >= 1 ? 'Да' : 'Нет') : '-',
        check.site_mentioned === null || check.site_mentioned === undefined ? '-' : check.site_mentioned ? 'Да' : 'Нет',
        getProjectName(check.project_id)
      ]);

      autoTable(doc, {
        head: [['Дата', 'Запрос', 'Регион', 'Поиск', 'Бренд', 'Сайт', 'Проект']],
        body: tableData,
        startY: yPos,
        styles: { fontSize: 8, font: 'Roboto', fontStyle: 'normal' },
        headStyles: { fillColor: [59, 130, 246], font: 'Roboto', fontStyle: 'normal' },
        margin: { top: 30, bottom: 25, left: 10, right: 10 },
        didDrawPage: (data) => addHeaderFooter(data.pageNumber, doc.internal.getNumberOfPages())
      });

      yPos = doc.lastAutoTable.finalY + 10;

      // -- AI РЕКОМЕНДАЦИИ --
      if (reportConfig.includeAIRecommendations) {
        const itemsWithRecs = dataForExport.filter(i => i.ai_recommendations && i.ai_recommendations.length > 10);
        if (itemsWithRecs.length > 0) {
          doc.addPage();
          yPos = 30;
          addHeaderFooter(doc.internal.getNumberOfPages(), doc.internal.getNumberOfPages());

          doc.setFontSize(16);
          doc.setTextColor(40);
          doc.text("AI Рекомендации и Анализ (Топ-50)", 10, yPos);
          yPos += 10;

          itemsWithRecs.slice(0, 50).forEach((item, idx) => {
            if (yPos > pageHeight - 40) {
              addHeaderFooter(doc.internal.getNumberOfPages(), doc.internal.getNumberOfPages() + 1);
              doc.addPage();
              yPos = 30;
            }

            doc.setFontSize(11);
            doc.setTextColor(0);
            doc.setFont("Roboto", "bold");
            doc.text(`${idx + 1}. ${item.query} (${getEngineText(item.search_engine)}) - Бренд: ${item.brand_score !== null ? (item.brand_score >= 1 ? 'Да' : 'Нет') : '-'} | Сайт: ${item.site_mentioned === null || item.site_mentioned === undefined ? '-' : item.site_mentioned ? 'Да' : 'Нет'}`, 10, yPos);
            yPos += 6;

            doc.setFont("Roboto", "normal");
            doc.setFontSize(10);
            doc.setTextColor(60);

            const recText = doc.splitTextToSize(item.ai_recommendations, pageWidth - 20);
            doc.text(recText, 10, yPos);
            yPos += (recText.length * 4) + 6;

            if (reportConfig.includeAIComments && item.ai_comment) {
              doc.setFont("Roboto", "normal");
              doc.text(`Комментарий: ${item.ai_comment.substring(0, 200)}...`, 10, yPos);
              yPos += 8;
            }
          });
        }
      }

      const totalPages = doc.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        addHeaderFooter(i, totalPages);
      }
      doc.save(`avi-report-${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (error) {
      console.error('Error exporting to PDF:', error);
      alert('Ошибка при экспорте в PDF: ' + error.message);
    } finally {
      setExporting(false);
    }
  };

  // Обработчик клика на кнопку Excel - открывает модальное окно
  const handleExcelExportClick = () => {
    setShowExcelConfigModal(true);
  };

  // Экспорт в Excel
  const exportToExcel = async () => {
    setShowExcelConfigModal(false);
    setExporting(true);
    try {
      // Функция для обрезки текста (Excel ограничение 32767 символов)
      const truncateText = (text, maxLength = 32000) => {
        if (!text) return '';
        const str = String(text);
        return str.length > maxLength ? str.substring(0, maxLength) + '...' : str;
      };

      // Подготовка данных с учетом выбранных полей
      const data = dataForExport.map(check => {
        const row = {};

        if (excelConfig.includeDate) {
          row['Дата'] = formatDate(check.check_date);
        }
        if (excelConfig.includeQuery) {
          row['Запрос'] = truncateText(check.query, 1000);
        }
        if (excelConfig.includeRegion) {
          row['Регион'] = check.region || '';
        }
        if (excelConfig.includeEngine) {
          row['Нейросеть'] = getEngineText(check.search_engine);
        }
        if (excelConfig.includeBrandScore) {
          row['Бренд'] = check.brand_score !== null ? (check.brand_score >= 1 ? 'Да' : 'Нет') : '';
        }
        if (excelConfig.includeSiteMentioned) {
          row['Сайт в выдаче'] = check.site_mentioned === null || check.site_mentioned === undefined
            ? '' : check.site_mentioned ? 'Найден' : 'Не найден';
        }
        if (excelConfig.includeProject) {
          row['Проект'] = getProjectName(check.project_id);
        }
        if (excelConfig.includeAliceResponse) {
          row['Упоминание'] = truncateText(check.alice_response, 10000);
        }
        if (excelConfig.includeAIComment) {
          row['Тональность'] = truncateText(check.ai_comment, 10000);
        }
        if (excelConfig.includeAIRecommendations) {
          row['Рекомендации'] = truncateText(check.ai_recommendations, 10000);
        }

        return row;
      });

      // Создаём книгу
      const wb = XLSX.utils.book_new();

      // Лист с данными
      const ws = XLSX.utils.json_to_sheet(data);

      // Настройка ширины колонок и переноса текста
      const colWidths = [];
      const headers = Object.keys(data[0] || {});

      headers.forEach(header => {
        switch (header) {
          case 'Дата':
            colWidths.push({ wch: 18 });
            break;
          case 'Запрос':
            colWidths.push({ wch: 40 });
            break;
          case 'Регион':
            colWidths.push({ wch: 20 });
            break;
          case 'Нейросеть':
            colWidths.push({ wch: 15 });
            break;
          case 'Бренд':
            colWidths.push({ wch: 12 });
            break;
          case 'Сайт в выдаче':
            colWidths.push({ wch: 14 });
            break;
          case 'Проект':
            colWidths.push({ wch: 25 });
            break;
          case 'Упоминание':
            colWidths.push({ wch: 50 });
            break;
          case 'Тональность':
            colWidths.push({ wch: 40 });
            break;
          case 'Рекомендации':
            colWidths.push({ wch: 40 });
            break;
          default:
            colWidths.push({ wch: 20 });
        }
      });

      ws['!cols'] = colWidths;

      // Включаем перенос текста для всех ячеек
      const range = XLSX.utils.decode_range(ws['!ref']);
      for (let R = range.s.r; R <= range.e.r; ++R) {
        for (let C = range.s.c; C <= range.e.c; ++C) {
          const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
          if (!ws[cellAddress]) continue;
          if (!ws[cellAddress].s) ws[cellAddress].s = {};
          ws[cellAddress].s.alignment = { wrapText: true, vertical: 'top' };
        }
      }

      XLSX.utils.book_append_sheet(wb, ws, 'Проверки');

      // Лист со статистикой
      const statsData = [
        { 'Показатель': 'Всего проверок', 'Значение': stats.total },
        { 'Показатель': 'Успешных', 'Значение': stats.completed },
        { 'Показатель': 'С ошибками', 'Значение': stats.errors },
        { 'Показатель': 'Бренд найден (из проверенных)', 'Значение': stats.brandCheckedCount > 0 ? `${stats.brandFoundCount} / ${stats.brandCheckedCount}` : 'Н/Д' },
        { 'Показатель': 'Сайт найден (из проверенных)', 'Значение': stats.siteCheckedCount > 0 ? `${stats.siteFoundCount} / ${stats.siteCheckedCount}` : 'Н/Д' },
        { 'Показатель': 'Дата отчёта', 'Значение': new Date().toLocaleDateString('ru-RU') }
      ];

      const wsStats = XLSX.utils.json_to_sheet(statsData);
      wsStats['!cols'] = [{ wch: 25 }, { wch: 20 }];
      XLSX.utils.book_append_sheet(wb, wsStats, 'Статистика');

      // Сохраняем файл
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(blob, `avi-report-${new Date().toISOString().split('T')[0]}.xlsx`);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      alert('Ошибка при экспорте в Excel');
    } finally {
      setExporting(false);
    }
  };

  // Экспорт в Word (DOCX)
  const exportToWord = async () => {
    setExporting(true);
    try {
      // Загружаем изображения
      let headerImgData = null;
      let footerImgData = null;

      try {
        const headerResponse = await fetch('/avi-header.png');
        headerImgData = await headerResponse.arrayBuffer();
      } catch (e) {
        console.warn('Could not load header image');
      }

      try {
        const footerResponse = await fetch('/avi-footer.png');
        footerImgData = await footerResponse.arrayBuffer();
      } catch (e) {
        console.warn('Could not load footer image');
      }

      // Создаём параграфы документа
      const children = [];

      // Заголовок с изображением
      // Оригинал header 1290x109, пропорция ~11.8:1
      // Полная ширина страницы Word ~600px, высота = 600/11.8 ≈ 51px
      if (headerImgData) {
        children.push(
          new Paragraph({
            children: [
              new ImageRun({
                data: headerImgData,
                transformation: { width: 600, height: 51 },
                type: 'png'
              })
            ],
            alignment: AlignmentType.LEFT
          })
        );
      }

      children.push(
        new Paragraph({
          text: 'Отчёт по проверкам поисковых систем',
          heading: HeadingLevel.HEADING_1,
          alignment: AlignmentType.CENTER,
          spacing: { before: 400, after: 200 }
        })
      );

      children.push(
        new Paragraph({
          text: `AVI Analytics | ${new Date().toLocaleDateString('ru-RU')}`,
          alignment: AlignmentType.CENTER,
          spacing: { after: 400 }
        })
      );

      // Статистика
      children.push(
        new Paragraph({
          text: 'Общая статистика',
          heading: HeadingLevel.HEADING_2,
          spacing: { before: 200, after: 100 }
        })
      );

      children.push(
        new Paragraph({
          children: [
            new TextRun({ text: `• Всего проверок: ${stats.total}`, break: 1 }),
            new TextRun({ text: `• Успешных: ${stats.completed}`, break: 1 }),
            new TextRun({ text: `• С ошибками: ${stats.errors}`, break: 1 }),
            new TextRun({ text: `• Бренд найден: ${stats.brandCheckedCount > 0 ? `${stats.brandFoundCount} из ${stats.brandCheckedCount}` : 'Н/Д'}`, break: 1 }),
            new TextRun({ text: `• Сайт найден: ${stats.siteCheckedCount > 0 ? `${stats.siteFoundCount} из ${stats.siteCheckedCount}` : 'Н/Д'}`, break: 1 })
          ],
          spacing: { after: 400 }
        })
      );

      // Таблица результатов
      children.push(
        new Paragraph({
          text: 'Результаты проверок',
          heading: HeadingLevel.HEADING_2,
          spacing: { before: 200, after: 100 }
        })
      );

      // Создаём строки таблицы
      // DXA: 1440 = 1 дюйм. Ширина страницы A4 ~9360 DXA (6.5 дюймов)
      // Распределяем колонки: Дата(1100) + Запрос(2600) + Регион(1200) + Поиск(900) + Score(1000) + Сайт(900) = 7700
      const colWidths = {
        date: 1100,
        query: 2600,
        region: 1200,
        search: 900,
        score: 1000,
        site: 900
      };

      const headerRow = new TableRow({
        children: [
          new TableCell({
            children: [new Paragraph({ text: 'Дата', alignment: AlignmentType.CENTER })],
            width: { size: colWidths.date, type: WidthType.DXA },
            shading: { fill: '3B82F6' }
          }),
          new TableCell({
            children: [new Paragraph({ text: 'Запрос', alignment: AlignmentType.CENTER })],
            width: { size: colWidths.query, type: WidthType.DXA },
            shading: { fill: '3B82F6' }
          }),
          new TableCell({
            children: [new Paragraph({ text: 'Регион', alignment: AlignmentType.CENTER })],
            width: { size: colWidths.region, type: WidthType.DXA },
            shading: { fill: '3B82F6' }
          }),
          new TableCell({
            children: [new Paragraph({ text: 'Поиск', alignment: AlignmentType.CENTER })],
            width: { size: colWidths.search, type: WidthType.DXA },
            shading: { fill: '3B82F6' }
          }),
          new TableCell({
            children: [new Paragraph({ text: 'Score', alignment: AlignmentType.CENTER })],
            width: { size: colWidths.score, type: WidthType.DXA },
            shading: { fill: '3B82F6' }
          }),
          new TableCell({
            children: [new Paragraph({ text: 'Сайт', alignment: AlignmentType.CENTER })],
            width: { size: colWidths.site, type: WidthType.DXA },
            shading: { fill: '3B82F6' }
          })
        ],
        tableHeader: true
      });

      const dataRows = dataForExport.slice(0, 100).map(check => new TableRow({
        children: [
          new TableCell({
            children: [new Paragraph({ text: formatDateShort(check.check_date) || '' })],
            width: { size: colWidths.date, type: WidthType.DXA }
          }),
          new TableCell({
            children: [new Paragraph({ text: check.query?.substring(0, 50) || '' })],
            width: { size: colWidths.query, type: WidthType.DXA }
          }),
          new TableCell({
            children: [new Paragraph({ text: check.region || '' })],
            width: { size: colWidths.region, type: WidthType.DXA }
          }),
          new TableCell({
            children: [new Paragraph({ text: getEngineText(check.search_engine) || '' })],
            width: { size: colWidths.search, type: WidthType.DXA }
          }),
          new TableCell({
            children: [new Paragraph({ text: check.brand_score !== null ? (check.brand_score >= 1 ? 'Да' : 'Нет') : '-' })],
            width: { size: colWidths.score, type: WidthType.DXA }
          }),
          new TableCell({
            children: [new Paragraph({ text: check.site_mentioned === null || check.site_mentioned === undefined ? '-' : check.site_mentioned ? '✓' : '✗' })],
            width: { size: colWidths.site, type: WidthType.DXA }
          })
        ]
      }));

      // Если нет данных, добавляем пустую строку
      const tableRows = dataRows.length > 0 ? [headerRow, ...dataRows] : [headerRow];

      const totalWidth = Object.values(colWidths).reduce((a, b) => a + b, 0);

      const table = new Table({
        rows: tableRows,
        width: { size: totalWidth, type: WidthType.DXA },
        columnWidths: [colWidths.date, colWidths.query, colWidths.region, colWidths.search, colWidths.score, colWidths.site]
      });

      children.push(table);

      // Примечание если данных больше 100
      if (dataForExport.length > 100) {
        children.push(
          new Paragraph({
            text: `Примечание: показаны первые 100 записей из ${dataForExport.length}`,
            spacing: { before: 200 },
            italics: true
          })
        );
      }

      // Подвал
      // Footer изображение - оригинал 1290x56, пропорция ~23:1
      // Полная ширина 600px, высота = 600/23 ≈ 26px
      children.push(
        new Paragraph({
          text: '',
          spacing: { before: 400 }
        })
      );

      if (footerImgData) {
        children.push(
          new Paragraph({
            children: [
              new ImageRun({
                data: footerImgData,
                transformation: { width: 600, height: 26 },
                type: 'png'
              })
            ],
            alignment: AlignmentType.LEFT
          })
        );
      }

      children.push(
        new Paragraph({
          text: '© AVI Analytics',
          alignment: AlignmentType.CENTER,
          spacing: { before: 100 }
        })
      );

      // Создаём документ
      const doc = new Document({
        sections: [{
          properties: {},
          children
        }]
      });

      // Сохраняем файл
      const buffer = await Packer.toBlob(doc);
      saveAs(buffer, `avi-report-${new Date().toISOString().split('T')[0]}.docx`);
    } catch (error) {
      console.error('Error exporting to Word:', error);
      alert('Ошибка при экспорте в Word');
    } finally {
      setExporting(false);
    }
  };

  // Функция для изменения выбранного проекта с сохранением в localStorage
  const handleProjectChange = (projectId) => {
    setSelectedProject(projectId);
    localStorage.setItem('selectedProject', projectId);
  };

  // Сброс фильтров
  const resetFilters = () => {
    handleProjectChange('all');
    setSelectedEngine('all');
    setDateFrom('');
    setDateTo('');
    setSearchQuery('');
  };

  if (loading) {
    return (
      <div className="reports loading-state">
        <div className="spinner"></div>
        <p>Загрузка данных...</p>
      </div>
    );
  }

  return (
    <div className="reports">
      <div className="reports-header">
        <div className="header-title">
          <FileText size={28} />
          <h1>Отчёты</h1>
        </div>
        <p className="header-description">
          Формирование и экспорт отчётов по результатам проверок
        </p>
      </div>

      {/* Скрытый контейнер для рендеринга графиков (Часть 1: Статистика + Тренды) */}
      <div style={{ position: 'absolute', left: '-9999px', top: 0, width: '1000px' }}>
        <div ref={chartsRef1}>
          <ReportCharts
            data={dataForExport}
            dateFrom={dateFrom}
            dateTo={dateTo}
            section={1}
          />
        </div>
      </div>

      {/* Скрытый контейнер для рендеринга графиков (Часть 2: Сравнение Google vs Яндекс) */}
      <div style={{ position: 'absolute', left: '-9999px', top: 1500, width: '1000px' }}>
        <div ref={chartsRef2}>
          <ReportCharts
            data={dataForExport}
            dateFrom={dateFrom}
            dateTo={dateTo}
            section={2}
          />
        </div>
      </div>

      {/* Модальное окно настроек отчёта */}
      {showConfigModal && (
        <div className="modal-overlay">
          <div className="modal-content report-config-modal">
            <div className="modal-header">
              <h3>Настройки отчёта</h3>
              <button className="close-btn" onClick={() => setShowConfigModal(false)}>
                <XCircle size={24} />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={reportConfig.includeCharts}
                    onChange={e => setReportConfig({ ...reportConfig, includeCharts: e.target.checked })}
                  />
                  Включить графики
                </label>
              </div>
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={reportConfig.includeAIRecommendations}
                    onChange={e => setReportConfig({ ...reportConfig, includeAIRecommendations: e.target.checked })}
                  />
                  Включить AI рекомендации
                </label>
              </div>
              {reportConfig.includeAIRecommendations && (
                <div className="form-group sub-option">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={reportConfig.includeAIComments}
                      onChange={e => setReportConfig({ ...reportConfig, includeAIComments: e.target.checked })}
                    />
                    Добавить комментарии AI
                  </label>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="cancel-btn" onClick={() => setShowConfigModal(false)}>Отмена</button>
              <button className="primary-btn" onClick={generatePDF}>
                {exporting ? 'Создание...' : 'Скачать PDF'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно настроек Excel экспорта */}
      {showExcelConfigModal && (
        <div className="modal-overlay">
          <div className="modal-content report-config-modal">
            <div className="modal-header">
              <h3>Настройки экспорта Excel</h3>
              <button className="close-btn" onClick={() => setShowExcelConfigModal(false)}>
                <XCircle size={24} />
              </button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: '15px', color: '#666', fontSize: '14px' }}>
                Выберите поля для экспорта:
              </p>
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={excelConfig.includeDate}
                    onChange={e => setExcelConfig({ ...excelConfig, includeDate: e.target.checked })}
                  />
                  Дата
                </label>
              </div>
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={excelConfig.includeQuery}
                    onChange={e => setExcelConfig({ ...excelConfig, includeQuery: e.target.checked })}
                  />
                  Запрос
                </label>
              </div>
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={excelConfig.includeRegion}
                    onChange={e => setExcelConfig({ ...excelConfig, includeRegion: e.target.checked })}
                  />
                  Регион
                </label>
              </div>
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={excelConfig.includeEngine}
                    onChange={e => setExcelConfig({ ...excelConfig, includeEngine: e.target.checked })}
                  />
                  Нейросеть
                </label>
              </div>
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={excelConfig.includeBrandScore}
                    onChange={e => setExcelConfig({ ...excelConfig, includeBrandScore: e.target.checked })}
                  />
                  Вхождение бренда
                </label>
              </div>
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={excelConfig.includeSiteMentioned}
                    onChange={e => setExcelConfig({ ...excelConfig, includeSiteMentioned: e.target.checked })}
                  />
                  Сайт в выдаче
                </label>
              </div>
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={excelConfig.includeProject}
                    onChange={e => setExcelConfig({ ...excelConfig, includeProject: e.target.checked })}
                  />
                  Проект
                </label>
              </div>
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={excelConfig.includeAliceResponse}
                    onChange={e => setExcelConfig({ ...excelConfig, includeAliceResponse: e.target.checked })}
                  />
                  Упоминание
                </label>
              </div>
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={excelConfig.includeAIComment}
                    onChange={e => setExcelConfig({ ...excelConfig, includeAIComment: e.target.checked })}
                  />
                  Тональность
                </label>
              </div>
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={excelConfig.includeAIRecommendations}
                    onChange={e => setExcelConfig({ ...excelConfig, includeAIRecommendations: e.target.checked })}
                  />
                  Рекомендации
                </label>
              </div>
            </div>
            <div className="modal-footer">
              <button className="cancel-btn" onClick={() => setShowExcelConfigModal(false)}>Отмена</button>
              <button className="primary-btn" onClick={exportToExcel}>
                {exporting ? 'Создание...' : 'Скачать Excel'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Статистика */}
      <div className="reports-stats">
        <div className="stat-card">
          <div className="stat-icon total">
            <BarChart3 size={24} />
          </div>
          <div className="stat-info">
            <span className="stat-value">{stats.total}</span>
            <span className="stat-label">Всего проверок</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon success">
            <CheckCircle size={24} />
          </div>
          <div className="stat-info">
            <span className="stat-value">{stats.completed}</span>
            <span className="stat-label">Успешных</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon error">
            <XCircle size={24} />
          </div>
          <div className="stat-info">
            <span className="stat-value">{stats.errors}</span>
            <span className="stat-label">С ошибками</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon score">
            <TrendingUp size={24} />
          </div>
          <div className="stat-info">
            <span className="stat-value">{stats.avgBrandScore ?? '—'}</span>
            <span className="stat-label">Средний Score</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon" style={{ backgroundColor: stats.siteFoundCount > 0 ? '#f0fff4' : '#fff1f0', color: stats.siteFoundCount > 0 ? '#52c41a' : '#ff4d4f' }}>
            <ExternalLink size={24} />
          </div>
          <div className="stat-info">
            <span className="stat-value">{stats.siteCheckedCount > 0 ? `${stats.siteFoundCount}/${stats.siteCheckedCount}` : '—'}</span>
            <span className="stat-label">Сайт найден</span>
          </div>
        </div>
      </div>

      {/* Фильтры */}
      <div className="reports-filters">
        <div className="filters-header">
          <Filter size={20} />
          <span>Фильтры</span>
          <button className="reset-filters-btn" onClick={resetFilters}>
            <RefreshCw size={16} />
            Сбросить
          </button>
        </div>

        <div className="filters-grid">
          <div className="reports-filter-group">
            <label>
              <Building2 size={16} />
              Проект
            </label>
            <select
              value={selectedProject}
              onChange={(e) => handleProjectChange(e.target.value)}
            >
              <option value="all">Все проекты</option>
              {projects.map(project => (
                <option key={project.id} value={project.id}>
                  {project.title}
                </option>
              ))}
            </select>
          </div>

          <div className="reports-filter-group">
            <label>
              <Search size={16} />
              Поисковая система
            </label>
            <select
              value={selectedEngine}
              onChange={(e) => setSelectedEngine(e.target.value)}
            >
              <option value="all">Все</option>
              <option value="google">Google</option>
              <option value="yandex">Яндекс</option>
            </select>
          </div>

          <div className="reports-filter-group">
            <label>
              <Calendar size={16} />
              Дата с
            </label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </div>

          <div className="reports-filter-group">
            <label>
              <Calendar size={16} />
              Дата по
            </label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </div>

          <div className="reports-filter-group search-group">
            <label>
              <Search size={16} />
              Поиск по запросу
            </label>
            <input
              type="text"
              placeholder="Введите текст запроса..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Кнопки экспорта (перемещено под фильтры) */}
      <div className="export-actions-bar">
        <button
          className={`export-btn selection-btn ${selectionMode ? 'active' : ''}`}
          onClick={() => selectionMode ? clearSelection() : setSelectionMode(true)}
        >
          <CheckSquare size={18} />
          {selectionMode ? 'Отменить выбор' : 'Выбрать запросы'}
          {selectedIds.size > 0 && ` (${selectedIds.size})`}
        </button>
        <button className="export-btn pdf-btn" onClick={handleExportClick} disabled={exporting}>
          <FileDown size={18} />
          PDF Отчёт{selectedIds.size > 0 && ` (${selectedIds.size})`}
        </button>
        <button className="export-btn excel-btn" onClick={handleExcelExportClick} disabled={exporting}>
          <FileSpreadsheet size={18} />
          Excel{selectedIds.size > 0 && ` (${selectedIds.size})`}
        </button>
        <button className="export-btn word-btn" onClick={exportToWord} disabled={exporting}>
          <FileType size={18} />
          Word{selectedIds.size > 0 && ` (${selectedIds.size})`}
        </button>
      </div>



      {/* Предпросмотр данных */}
      <div className="data-preview">
        <div className="preview-header">
          <h2>
            <Eye size={20} />
            Предпросмотр данных
          </h2>
          <span className="records-count">{filteredData.length} записей</span>
        </div>

        {filteredData.length === 0 ? (
          <div className="no-data">
            <AlertCircle size={48} />
            <p>Нет данных для отображения</p>
            <span>Попробуйте изменить параметры фильтрации</span>
          </div>
        ) : (
          <div className="preview-table-container">
            <table className="preview-table">
              <thead>
                <tr>
                  {selectionMode && (
                    <th style={{ width: '40px', textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={selectedIds.size === filteredData.length && filteredData.length > 0}
                        onChange={toggleSelectAll}
                        title="Выбрать все"
                      />
                    </th>
                  )}
                  <th>Дата</th>
                  <th>Запрос</th>
                  <th>Регион</th>
                  <th>Поиск</th>
                  <th>Score</th>
                  <th>Сайт</th>
                  <th>Проект</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.slice(0, showAllRecords ? filteredData.length : 20).map((check, index) => (
                  <tr key={check.id || index} className={selectedIds.has(check.id) ? 'selected' : ''}>
                    {selectionMode && (
                      <td style={{ textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(check.id)}
                          onChange={() => toggleSelectItem(check.id)}
                        />
                      </td>
                    )}
                    <td data-label="Дата">{formatDateShort(check.check_date)}</td>
                    <td className="query-cell" data-label="Запрос" title={check.query}>
                      {check.query?.substring(0, 40)}{check.query?.length > 40 ? '...' : ''}
                    </td>
                    <td data-label="Регион">{check.region}</td>
                    <td data-label="Поиск">
                      <span className={`engine-badge ${check.search_engine}`}>
                        {getEngineText(check.search_engine)}
                      </span>
                    </td>
                    <td data-label="Бренд">
                      {check.brand_score !== null ? (
                        check.brand_score >= 1 ? (
                          <span className="score-badge high" title="Бренд найден">✓</span>
                        ) : (
                          <span className="score-badge low" title="Бренд не найден">✗</span>
                        )
                      ) : '—'}
                    </td>
                    <td data-label="Сайт">
                      {check.site_mentioned === null || check.site_mentioned === undefined ? '—' :
                        check.site_mentioned ? (
                          <span className="score-badge high" title="Сайт найден в выдаче">✓</span>
                        ) : (
                          <span className="score-badge low" title="Сайт не найден">✗</span>
                        )
                      }
                    </td>
                    <td className="project-cell" data-label="Проект">{getProjectName(check.project_id)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {filteredData.length > 20 && (
              <div className="table-footer">
                {showAllRecords ? (
                  <>
                    Показаны все {filteredData.length} записей
                    <button
                      className="view-all-btn"
                      onClick={() => setShowAllRecords(false)}
                    >
                      <Eye size={16} />
                      Показать первые 20
                    </button>
                  </>
                ) : (
                  <>
                    Показаны первые 20 из {filteredData.length} записей
                    <button
                      className="view-all-btn"
                      onClick={() => setShowAllRecords(true)}
                    >
                      <Eye size={16} />
                      Посмотреть все
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Reports;
